import React, { useEffect, useMemo, useState } from 'react';
import { loadPageADay } from '../utils/pageADay';
import AffiliateSidebarRail from './ads/AffiliateSidebarRail';

export default function PageADaySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPageIndex, setSelectedPageIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await loadPageADay();
        if (!cancelled) {
          setData(result);
          setSelectedPageIndex(
            result?.program?.current_page != null ? result.program.current_page : null,
          );
        }
      } catch (err) {
        console.error('Failed to load page a day', err);
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const finishedPages = data?.finished_pages || [];
  const program = data?.program;
  const todaySnapshot = data?.snapshot;

  const selectedView = useMemo(() => {
    if (!program || selectedPageIndex == null) {
      return null;
    }
    if (selectedPageIndex === program.current_page && todaySnapshot) {
      return {
        pageIndex: program.current_page,
        dayIndex: program.day_index,
        snapshot: todaySnapshot,
        isToday: true,
      };
    }
    const past = finishedPages.find((p) => p.page_index === selectedPageIndex);
    if (past?.snapshot) {
      return {
        pageIndex: past.page_index,
        dayIndex: past.day_index,
        snapshot: past.snapshot,
        isToday: false,
      };
    }
    if (todaySnapshot) {
      return {
        pageIndex: program.current_page,
        dayIndex: program.day_index,
        snapshot: todaySnapshot,
        isToday: true,
      };
    }
    return null;
  }, [finishedPages, program, selectedPageIndex, todaySnapshot]);

  if (loading) {
    return (
      <section className="relative px-6 py-16 max-w-full bg-slate-50">
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="w-8 h-8 border-4 border-violet-800 border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (!data?.available || !todaySnapshot?.snap_shot_url || !program) {
    return null;
  }

  const book = program.book || {};
  const bookName = book.book_name || "Today's reading";
  const displaySnapshot = selectedView?.snapshot || todaySnapshot;
  const displayDayIndex = selectedView?.dayIndex ?? program.day_index;

  return (
    <section className="relative px-3 md:px-4 py-16 max-w-full bg-slate-50">
      <div className="relative w-full">
        {finishedPages.length > 0 && (
          <aside className="w-full md:w-28 lg:w-32 md:absolute md:left-2 lg:left-4 md:top-0 md:bottom-0 md:pt-2 z-10 mb-6 md:mb-0 md:flex md:flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 text-center shrink-0">
              Finished pages
            </p>
            <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible md:overflow-y-auto md:min-h-0 md:flex-1 pb-2 md:pb-0 md:pr-1">
              {[...finishedPages].reverse().map((page) => {
                const isSelected = selectedPageIndex === page.page_index;
                const url = page.snapshot?.snap_shot_url;
                if (!url) return null;
                return (
                  <button
                    key={page.page_index}
                    type="button"
                    onClick={() => setSelectedPageIndex(page.page_index)}
                    className={`flex-shrink-0 w-20 md:w-full text-left rounded overflow-hidden border-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-900 ${
                      isSelected
                        ? 'border-violet-900 shadow-md'
                        : 'border-transparent hover:border-violet-300'
                    }`}
                    aria-pressed={isSelected}
                    aria-label={`View day ${page.day_index}`}
                  >
                    <img
                      src={url}
                      alt={`Day ${page.day_index}`}
                      className="w-full h-24 md:h-28 object-cover object-top bg-white"
                    />
                    <span className="block text-[11px] text-center py-1 bg-white text-gray-600">
                      Day {page.day_index}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedView && !selectedView.isToday && (
              <button
                type="button"
                onClick={() => setSelectedPageIndex(program.current_page)}
                className="mt-3 w-full text-sm text-violet-900 font-medium hover:underline text-center shrink-0"
              >
                Back to today
              </button>
            )}
          </aside>
        )}

        <div className="flex flex-col items-center min-w-0 text-center mx-auto max-w-3xl px-2">
          <p className="text-xs tracking-wide text-gray-400 mb-3">
            Reading encouragement campaign
          </p>
          <p className="text-sm font-semibold tracking-wide uppercase text-violet-900 mb-2">
            A page a day
          </p>
          <h2 className="text-3xl font-bold mb-3">Build a reading habit</h2>
          <p className="text-gray-600 mb-6">
            One page from a classic each day — easy to keep, hard to quit.
          </p>

          <p className="text-lg font-medium text-gray-800 mb-2">{bookName}</p>
          {displayDayIndex != null && program.total_days != null && (
            <p className="text-sm text-gray-500 mb-6">
              {selectedView && !selectedView.isToday ? 'Viewing ' : ''}
              Day {displayDayIndex} of {program.total_days}
              {selectedView && !selectedView.isToday ? ' (past)' : ''}
            </p>
          )}

          <img
            src={displaySnapshot.snap_shot_url}
            alt={`Page from ${bookName}`}
            className="max-w-full w-full shadow-md rounded-md bg-white"
          />
        </div>

        <aside className="hidden md:block absolute right-2 lg:right-4 top-0 pt-2 z-10">
          <AffiliateSidebarRail
            placement="page_a_day_right"
            salt={13}
            productCount={8}
            size="compact"
            desktopOnly={false}
          />
        </aside>
      </div>
    </section>
  );
}
